import React from 'react';
import { toast } from 'sonner';
import { SectionCard } from '../../components/sections/SectionCard';
import { PanelTable } from '../../components/display/PanelTable';
import { AppStatusBadge } from '@shared/components/ui/status-badge/AppStatusBadge';
import type { PanelTableColumn } from '../../components/display/PanelTable/types';
import type { BillingSchedule, BillingScheduleInvoiceData } from '@features/billing-schedules/types/billing-schedule.types';

interface InvoicesSectionProps {
  isDetailView?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  billingSchedule?: BillingSchedule;
  className?: string;
}

const renderTotalWithStatus = (_: unknown, item: BillingScheduleInvoiceData) => {
  const parts = item.total.split(' USD');
  const amount = parts[0];

  return (
    <div className="flex items-center gap-2">
      <span className="font-medium">
        {amount} <span className="font-normal text-teal-300">USD</span>
      </span>
      <AppStatusBadge
        entityType="invoice"
        status={item.status}
        size="sm"
      />
    </div>
  );
};

const columns: PanelTableColumn<BillingScheduleInvoiceData>[] = [
  {
    key: 'total',
    label: 'TOTAL',
    headerClassName: 'w-[140px]',
    render: renderTotalWithStatus
  },
  {
    key: 'invoiceNumber',
    label: 'INVOICE #',
    headerClassName: 'w-[110px]',
    cellClassName: 'font-medium'
  },
  {
    key: 'billingPeriod',
    label: 'BILLING PERIOD',
    headerClassName: 'w-[120px]'
  },
  {
    key: 'sent',
    label: 'SENT',
    headerClassName: 'w-[90px]',
    cellClassName: 'whitespace-nowrap'
  },
  {
    key: 'due',
    label: 'DUE',
    headerClassName: 'w-[90px]',
    cellClassName: 'whitespace-nowrap'
  },
  {
    key: 'finalized',
    label: 'FINALIZED',
    headerClassName: 'w-[90px]',
    cellClassName: 'whitespace-nowrap'
  }
];

const rowActions = (item: BillingScheduleInvoiceData) => [
  {
    key: 'send-invoice',
    label: 'Send invoice',
    onClick: () => { toast.info(`Send invoice ${item.invoiceNumber} — not yet implemented`) }
  },
  {
    key: 'finalize-invoice',
    label: 'Finalize invoice',
    onClick: () => { toast.info(`Finalize invoice ${item.invoiceNumber} — not yet implemented`) }
  },
  {
    key: 'download-invoice',
    label: 'Download invoice',
    onClick: () => { toast.info(`Download invoice ${item.invoiceNumber} — not yet implemented`) }
  }
];

export const InvoicesSection = React.memo<InvoicesSectionProps>(({
  isDetailView = false,
  isExpanded = true,
  onToggle,
  billingSchedule,
  className,
}) => {
  const invoiceData = billingSchedule?.invoices ?? [];

  return (
    <SectionCard
      title="Invoices"
      layout="down"
      isCollapsible={!isDetailView}
      isCollapsed={!isExpanded}
      onTitleClick={onToggle}
      className={className}
    >
      <PanelTable
        data={invoiceData}
        columns={columns}
        variant={isDetailView ? 'full' : 'panel'}
        emptyMessage="No invoices found"
        pageSize={5}
        rowActions={rowActions}
      />
    </SectionCard>
  );
});

InvoicesSection.displayName = 'InvoicesSection';
